import { useMemo, useState } from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import moment from "moment";

import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable/DataTable";
import { stripHtml } from "utils/stripHtml";

export const ConsultationNotesTable = ({
  notes = [],
  currentNoteId,
  currentStaffMemberId,
  onEdit,
  onDelete,
}) => {
  const [noteToDelete, setNoteToDelete] = useState(null);

  const COLUMNS = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "created",
        width: "200px",
        render: (row) => {
          if (!row.created) return "—";
          const parsed = moment(row.created, moment.ISO_8601, true);
          return parsed.isValid()
            ? parsed.format("DD-MM-YYYY HH:mm")
            : row.created;
        },
      },
      {
        header: "Consultation Professional",
        accessorKey: "staffMember",
        width: "250px",
        flexGrow: 1,
        render: (row) => {
          const staff = row.createdByStaffMember || row.staffMember;
          if (!staff) return "—";
          return (
            `${staff.firstName ?? ""} ${staff.lastName ?? ""}`.trim() || "—"
          );
        },
      },
      {
        header: "Note",
        accessorKey: "note",
        width: "1000px",
        render: (row) => {
          const plain = stripHtml(row.note);
          return plain
            ? plain.substring(0, 200) + (plain.length > 200 ? "..." : "")
            : "—";
        },
      },
    ],
    []
  );

  const getNoteCreatorId = (note) =>
    note?.createdByStaffMember?.id ?? note?.staffMemberId;

  const isNoteOwnedByCurrentUser = (note) => {
    if (!currentStaffMemberId) return true;
    return String(getNoteCreatorId(note)) === String(currentStaffMemberId);
  };

  const rowActions = useMemo(
    () => [
      {
        icon: Pencil,
        label: "Edit note",
        onClick: onEdit,
        primary: true,
        disabled: (row) => row.id === currentNoteId,
        visibleWhen: (row) => !!onDelete && isNoteOwnedByCurrentUser(row),
      },
      {
        icon: Eye,
        label: "View note",
        onClick: onEdit,
        primary: true,
        disabled: (row) => row.id === currentNoteId,
        visibleWhen: (row) => !onDelete || !isNoteOwnedByCurrentUser(row),
      },
      ...(onDelete
        ? [
            {
              icon: Trash2,
              label: "Delete note",
              primary: true,
              onClick: (row) => setNoteToDelete(row),
              disabled: (row) => notes.length <= 1 || !isNoteOwnedByCurrentUser(row),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEdit, onDelete, notes.length, currentStaffMemberId]
  );

  return (
    <>
      <ConfirmDialog
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={() => {
          onDelete(noteToDelete);
          setNoteToDelete(null);
        }}
        title="Delete note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
      />
      <DataTable
        columns={COLUMNS}
        data={notes}
        getRowId={(row) => row.id}
        getRowClassName={(row) => (row.id === currentNoteId ? "active" : "")}
        onRowClick={(row) => {
          if (row.id !== currentNoteId) onEdit(row);
        }}
        rowActions={rowActions}
        rowActionsLabel="Note actions"
        emptyTitle="No notes found"
        emptyText="No notes have been added to this consultation."
      />
    </>
  );
};
